import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import loadable, { LoadableComponent } from '@loadable/component';
import React, { useCallback, useEffect, useMemo } from 'react';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { useCurrentCity } from '@cp/entities/location';
import { useClientWebResolver, useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { PageSkeleton } from '@cp/ds/src/components/pageSkeleton';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useGetQueryParams, useUpdateQueryParams } from '@cp/shared/queryParams';
import { ClientWebPageQueryParams } from '@common/model/meta/clientWebPageQueryParams';
import { useRouter } from '@cp/shared/router';
import { useDispatch } from 'react-redux';
import { catalogTypeToSuitableClientType } from '@common/utils/catalogType';
import { CatalogEvents, catalogLayoutSlice } from 'src/features/catalog';
import { useWithTrack } from 'src/shared/tracking';
import { useClientSelectionService } from 'src/features/clientSelection';
import { useExperimentMatch } from 'src/shared/ab';
import { CITY_PATH_PARAMETER } from '../city';
import { useCatalogQSDecoder } from './lib';
import { CatalogMetaTags } from './components/meta';
import { CatalogPageParams, CatalogViewMode, CatalogViewProps } from './types';
import { buildCatalogListRequest } from './utils/buildCatalogListRequest';
import { useCatalogFilter } from './hooks/useCatalogFilter';
import { SubcatalogPage } from './ui/subcatalog';
import { useIsSubcatalog } from './model';
import { useNeedHelpTimingModalTrigger } from './lib/useNeedHelpTrigger';
import { useCatalogSorters } from './hooks/useCatalogSorters';

const CatalogResumeMobileViewV2 = loadable(() => import('./view.resume.mobile.new'));
const CatalogResumeDesktopViewV2 = loadable(() => import('./view.resume.desktop.new'));
const CatalogResumeMobileView = loadable(() => import('./view.resume.mobile'));
const CatalogResumeDesktopView = loadable(() => import('./view.resume.desktop'));
const CatalogVacancyMobileView = loadable(() => import('./view.vacancy.mobile'));
const CatalogVacancyDesktopView = loadable(() => import('./view.vacancy.desktop'));

const CatalogPage: React.FC<CatalogPageParams> = (props) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const decoder = useCatalogQSDecoder();
  const resolver = useClientWebResolver();
  const updateQueryParams = useUpdateQueryParams();
  const { pageProps, cityAlias } = useClientWebRouteParams<ClientWebPage.Catalog>();
  const queryParams = useGetQueryParams<ClientWebPageQueryParams<ClientWebPage.Catalog>>(null, decoder);
  const { filter, setFilter } = useCatalogFilter({ pageProps, queryParams, updateQueryParams });
  const { sorting, onSorting } = useCatalogSorters({ queryParams, updateQueryParams });
  const clientSelectionService = useClientSelectionService();
  const { currentCity } = useCurrentCity();
  const isSubcatalog = useIsSubcatalog();
  const catalogVersion = useExperimentMatch('catalog', 'v2') ? 'v2' : 'default';

  const CATALOG_VIEW_MATRIX: Record<
    CatalogItemType,
    Record<'v2' | 'default', [LoadableComponent<CatalogViewProps>, LoadableComponent<CatalogViewProps>]>
  > = {
    [CatalogItemType.Resume]: {
      'v2': [CatalogResumeDesktopViewV2, CatalogResumeMobileViewV2],
      'default': [CatalogResumeDesktopView, CatalogResumeMobileView],
    },
    [CatalogItemType.Vacancy]: {
      'v2': [CatalogVacancyDesktopView, CatalogVacancyMobileView],
      'default': [CatalogVacancyDesktopView, CatalogVacancyMobileView],
    },
  };

  useEffect(() => {
    clientSelectionService.register({
      catalogType: pageProps.catalogType,
      careerId: pageProps.careerId,
      clientType: catalogTypeToSuitableClientType(pageProps.catalogType),
      cityId: currentCity.id,
    });
  }, [clientSelectionService, pageProps.catalogType, pageProps.careerId, currentCity.id]);

  useNeedHelpTimingModalTrigger({ catalogType: pageProps.catalogType, careerId: pageProps.careerId, cityId: currentCity.id });

  const listRequest = React.useMemo(
    () => buildCatalogListRequest({ pageProps, queryParams, cityId: Number(currentCity?.id) }),
    [currentCity?.id, pageProps, queryParams],
  );

  const isInitialRenderForScrollToTop = React.useRef(true);
  React.useEffect(() => {
    if (isInitialRenderForScrollToTop.current) {
      isInitialRenderForScrollToTop.current = false;
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [pageProps.catalogType, pageProps.careerId, queryParams.page]);

  const pageLinkGenerator = React.useCallback(
    (targetPage: number) => {
      return String(
        new URL(
          resolver.resolve({
            page: ClientWebPage.Catalog,
            pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
            pageProps,
            queryParams: {
              filter: queryParams.filter,
              page: targetPage === 1 ? undefined : targetPage,
            },
          }),
          router.location.origin,
        ),
      );
    },
    [resolver, cityAlias, pageProps, queryParams.filter, router.location.origin],
  );

  const handleChangePage = React.useCallback(
    (nextPage: number) => {
      updateQueryParams({ page: nextPage === 1 ? undefined : nextPage });
    },
    [updateQueryParams],
  );

  const handleChangeCity = React.useCallback(
    (newCity: CityViewModel) => {
      return router.navigate(
        resolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps,
          pathParams: { [CITY_PATH_PARAMETER]: newCity.alias },
          queryParams: { ...queryParams, page: undefined },
        }),
      );
    },
    [router, resolver, queryParams, pageProps],
  );
  const withTrack = useWithTrack();
  const handleViewModeChange = useCallback(
    (newViewState: CatalogViewMode) => {
      dispatch(catalogLayoutSlice.actions.setDesktopViewMode(newViewState));
    },
    [dispatch],
  );

  const onChangeViewMode = useMemo(() => withTrack(CatalogEvents.ViewModeChange, handleViewModeChange), [handleViewModeChange, withTrack]);
  const onChangeCity = useMemo(() => withTrack(() => CatalogEvents.FilterCityChange(), handleChangeCity), [handleChangeCity, withTrack]);

  const View = isSubcatalog ? SubcatalogPage : CATALOG_VIEW_MATRIX[pageProps.catalogType][catalogVersion][Number(isMobile)];

  const viewProps: CatalogViewProps = {
    currentCity,
    pageProps,
    listRequest,
    filter,
    sorting,
    onChangeSorting: onSorting,
    onChangePage: handleChangePage,
    onChangeCity,
    onChangeFilter: setFilter,
    onChangeViewMode,
    pageLinkGenerator,
  };

  return (
    <React.Fragment>
      {isSubcatalog || <CatalogMetaTags listRequest={listRequest} pageProps={pageProps} />}
      <View {...viewProps} fallback={<PageSkeleton />} />
    </React.Fragment>
  );
};

export default CatalogPage;
